import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import { toast } from "react-toastify";
import { getSettingValue } from "modules/PermissionModule";
import { updateSettings } from "modules/ConfigurationThunks";
import { GetMeetingJoinURL } from "Utils";
import {
  getFormattedTime,
  getDateTimeDifference,
  getDateMomentObject,
} from "Utils/DateTimeHelpers";
import useI18n from "Hooks/UseI18n";
import { useDispatch } from "Hooks/useDispatch";
import { USER_DISMISSED_TOAST_PREFERENCE } from "Constants/permissionConstants";
import {
  isEventDismissed,
  addDismissedEventId,
  isParentPartOfMeeting,
} from "./UpcomingMeetingNotificationUtils";
import useUpcomingMeetings from "./useUpcomingMeetings";
import { isUpcomingMeetingRouteAllowed } from "./upcomingMeetingRouteRules";

const TOAST_DISPATCH_GAP_MS = 250;
const COUNTDOWN_INTERVAL_MS = 60 * 1000; // Update countdown every minute

const getMeetingStatusText = (startTime, currentTime, t) => {
  const formattedTime = getFormattedTime(startTime, "h:mm A");

  // Use seconds for precision — moment.diff("minutes") truncates to an
  // integer, so _.ceil on that result is a no-op and causes "0 min" bugs.
  const secondsUntilStart = getDateTimeDifference({
    date1: startTime,
    date2: currentTime,
    differenceBy: "seconds",
  });

  if (secondsUntilStart <= 0) {
    return t("calendar:started_at_time", { time: formattedTime });
  }

  // ceil so 1m 15s → 2 min, 45s → 1 min. Floor of 1 so it never shows "0 min".
  const minutesUntilStart = Math.max(_.ceil(secondsUntilStart / 60), 1);

  return t("calendar:scheduled_for_starting_in", {
    time: formattedTime,
    minutes: minutesUntilStart,
  });
};

/**
 * A live-updating message component passed as the toast `msg`.
 *
 * Because react-toastify renders React elements (not just strings),
 * this component can manage its own state and re-render every minute.
 * This keeps the "Starting in X min" countdown accurate without
 * needing to tear down and recreate the toast.
 */
const MeetingCountdown = ({ startTime }) => {
  const { t } = useI18n(["calendar"]);
  const [currentTime, setCurrentTime] = useState(getDateMomentObject());

  useEffect(() => {
    let intervalId;

    // Wait until the next minute boundary (:00 seconds), then start a
    // 60-second interval so updates are aligned with the clock.
    const msUntilNextMinute =
      COUNTDOWN_INTERVAL_MS -
      (getDateMomentObject().valueOf() % COUNTDOWN_INTERVAL_MS);

    const timerId = setTimeout(() => {
      setCurrentTime(getDateMomentObject());
      intervalId = setInterval(
        () => setCurrentTime(getDateMomentObject()),
        COUNTDOWN_INTERVAL_MS
      );
    }, msUntilNextMinute);

    return () => {
      clearTimeout(timerId);
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  return getMeetingStatusText(startTime, currentTime, t);
};

const UpcomingMeetingNotification = ({
  userId,
  userLoggedIn,
  userEntityType,
  academicYearId,
  setToastMsg,
  openLinkInNewTab,
  childID,
  pathname,
}) => {
  // Parents query as their child (STUDENT). This follows the same parent-as-student
  // pattern used by EventModal (Enhancer.js getUserId/getUserEntityType).
  const isParent = userEntityType === "FAMILY_MEMBER";
  const queryUserId = isParent && childID ? childID : userId;
  const queryUserEntityType = isParent && childID ? "STUDENT" : userEntityType;

  const isRouteAllowed = isUpcomingMeetingRouteAllowed(pathname);

  const shouldFetchMeetings =
    userLoggedIn &&
    queryUserId &&
    queryUserEntityType &&
    academicYearId &&
    isRouteAllowed;
  const dispatch = useDispatch();

  const { upcomingMeetingsData } = useUpcomingMeetings({
    userId: queryUserId,
    userEntityType: queryUserEntityType,
    academicYearId,
    skip: !shouldFetchMeetings,
  });

  // Tracks event IDs for which a toast has already been dispatched in this
  // session. Prevents duplicate toasts when `data` changes due to an Ably
  // refetch or Apollo cache update returning the same events.
  // Uses a ref so it persists across re-renders without causing them.
  const toastedEventIdsRef = useRef({});

  // Clear live toasts on route-disable so the next return refetches cleanly.
  useEffect(() => {
    if (isRouteAllowed) return;
    _.forEach(toastedEventIdsRef.current, ({ toastId }) => {
      toast.dismiss(toastId);
    });
    toastedEventIdsRef.current = {};
  }, [isRouteAllowed]);

  /**
   * Handles dismissing a meeting toast by persisting the event ID
   * to the user preference. Also prunes IDs older than 24 hours
   * on each save to keep the stored value compact.
   *
   * Uses the default mutationVariablesSrc (UPDATE_USER_SETTINGS) which
   * saves at USER level — same pattern as Common:UserSelectedCalendarTags.
   */
  const handleDismiss = useCallback(
    eventId => {
      // Read current dismissed IDs from Apollo cache.
      // readFromUserAndOrgLevel reads from the ORGANIZATION_LEVEL cache
      // which includes user-specific overrides.
      const currentDismissedData =
        getSettingValue({
          name: USER_DISMISSED_TOAST_PREFERENCE,
          userId,
          readFromUserAndOrgLevel: true,
        }) || {};

      // Add new ID and prune expired entries (>24 hours old)
      const updatedData = addDismissedEventId(currentDismissedData, eventId);

      // Persist to user preference (same pattern as UserSelectedCalendarTags)
      dispatch(
        updateSettings({
          settings: [
            {
              name: USER_DISMISSED_TOAST_PREFERENCE,
              value: JSON.stringify(updatedData),
            },
          ],
          showToastMsg: false, // don't show a success toast for this background save
        })
      );
    },
    [dispatch, userId]
  );

  useEffect(() => {
    if (!shouldFetchMeetings) {
      return;
    }

    const events = _.get(upcomingMeetingsData, "node.calendarEvents", []);
    const currentEventIds = _.map(events, "id");

    // Auto-dismiss toasts for events the BE no longer returns, or whose
    // details (title/URL/participant status) have changed. Each toast gets a
    // unique toastId via _.uniqueId, so dismissing the old and creating a new
    // one avoids react-toastify's same-ID animation race condition.
    _.forEach(
      toastedEventIdsRef.current,
      (
        {
          toastId: storedToastId,
          title,
          joinUrl: storedUrl,
          isPartOfMeeting: storedIsPartOfMeeting,
        },
        eventId
      ) => {
        if (!_.includes(currentEventIds, eventId)) {
          toast.dismiss(storedToastId);
          toastedEventIdsRef.current = _.omit(
            toastedEventIdsRef.current,
            eventId
          );
          return;
        }

        const event = _.find(
          events,
          singleEvent => _.get(singleEvent, "id") === eventId
        );
        const meeting = _.get(event, "item.meeting", null);
        const currentTitle = _.get(event, "item.label", "");
        const currentUrl = GetMeetingJoinURL({ meeting, userId });

        // For parents, re-evaluate participation status on every refetch.
        // If a parent is removed from custom participants (or sharedWith
        // changes), the toast must be dismissed immediately.
        const currentIsPartOfMeeting = isParent
          ? isParentPartOfMeeting({
              sharedWith: _.get(event, "item.sharedWith", []),
              associatedEntities: _.get(event, "item.associatedEntities", []),
              users: _.get(event, "item.users", []),
              parentUserId: userId,
            })
          : true;

        if (
          !_.isEqual(currentTitle, title) ||
          !_.isEqual(currentUrl, storedUrl) ||
          !_.isEqual(currentIsPartOfMeeting, storedIsPartOfMeeting)
        ) {
          toast.dismiss(storedToastId);
          toastedEventIdsRef.current = _.omit(
            toastedEventIdsRef.current,
            eventId
          );
        }
      }
    );

    /**
     * Read dismissed IDs from the Apollo cache.
     *
     * No race condition: ChildrenPage (our parent) uses withLoader which
     * blocks rendering until all bulk preference queries complete and
     * populate the cache. By the time this component mounts, the preference
     * value is already available — same pattern as Common:UserSelectedCalendarTags.
     */
    const dismissedData =
      getSettingValue({
        name: USER_DISMISSED_TOAST_PREFERENCE,
        userId,
        readFromUserAndOrgLevel: true,
      }) || {};

    // Filter out:
    // 1. Events the user explicitly dismissed (persisted in user preferences)
    // 2. Events we've already shown a toast for in this session (in-memory ref)
    // 3. For parents: events where the parent is not a participant
    //
    // (1) survives page refreshes; (2) handles refetches within the same session.
    // If a meeting is edited (time changes), the event ID changes because the
    // timestamp is embedded in it, so the edited meeting gets a fresh toast.
    //
    // (3) is needed because parents query as their child (STUDENT), so the BE
    // returns all of the child's + parent's meetings. We must then client-side filter to
    // only show meetings the parent is actually invited to — checking both
    // grade/course-level sharing (sharedWith) and custom participant lists
    // (users), matching EventModal's getCanUserJoinMeeting + associatedEntities
    // pattern.
    const activeEvents = _.filter(
      events,
      event =>
        !isEventDismissed(dismissedData, _.get(event, "id")) &&
        !toastedEventIdsRef.current[_.get(event, "id")] &&
        (!isParent ||
          isParentPartOfMeeting({
            sharedWith: _.get(event, "item.sharedWith", []),
            associatedEntities: _.get(event, "item.associatedEntities", []),
            users: _.get(event, "item.users", []),
            parentUserId: userId,
          }))
    );

    const timerIds = _.map(activeEvents, (event, index) => {
      const eventId = _.get(event, "id");
      const meetingTitle = _.get(event, "item.label", "Untitled meeting");
      const meetingStartTime = _.get(event, "startTime", "");
      const meeting = _.get(event, "item.meeting", null);

      // GetMeetingJoinURL handles Zoom creator logic (startUrl vs joinUrl)
      // and returns the correct URL based on meeting type and user role.
      const joinUrl = GetMeetingJoinURL({ meeting, userId });

      const toastId = _.uniqueId("meeting_toast_");

      const toastPayload = {
        title: meetingTitle,
        // MeetingCountdown is a React component that re-renders every minute,
        // keeping the "Starting in X min" text accurate over time.
        msg: <MeetingCountdown startTime={meetingStartTime} />,
        notString: true,
        type: "info",
        persistent: true,
        toastId,
        // onDismiss is called when the user clicks the close button on the
        // persistent notification. It saves the event ID to the user preference
        // so the toast won't reappear after a page refresh.
        onDismiss: () => handleDismiss(eventId),
        ...(joinUrl
          ? {
              primaryAction: {
                label: "common:join_now",
                onClick: () => {
                  openLinkInNewTab({ url: joinUrl });
                },
              },
            }
          : {}),
      };

      // Store toast details so we can detect title/URL/participation changes
      // on refetch. For parents, isPartOfMeeting lets us dismiss the toast if
      // the parent is later removed from the meeting's participant list.
      toastedEventIdsRef.current[eventId] = {
        toastId,
        title: meetingTitle,
        joinUrl: joinUrl || "",
        isPartOfMeeting: true,
      };

      // LoginReducer keeps only one `toast` object in state, and LoginView reads
      // and clears it after rendering. If we dispatch multiple toasts back-to-back,
      // later payloads can overwrite earlier ones before they are consumed.
      // A 250ms stagger gives each dispatch enough time to render and clear safely.
      return setTimeout(() => {
        setToastMsg(toastPayload);
      }, index * TOAST_DISPATCH_GAP_MS);
    });

    return () => {
      _.forEach(timerIds, clearTimeout);
    };
  }, [
    upcomingMeetingsData,
    shouldFetchMeetings,
    openLinkInNewTab,
    setToastMsg,
    handleDismiss,
    isParent,
    userId,
  ]);

  return null;
};

MeetingCountdown.propTypes = {
  startTime: PropTypes.string.isRequired,
};

UpcomingMeetingNotification.propTypes = {
  userId: PropTypes.string.isRequired,
  userLoggedIn: PropTypes.bool.isRequired,
  userEntityType: PropTypes.string.isRequired,
  academicYearId: PropTypes.string.isRequired,
  setToastMsg: PropTypes.func.isRequired,
  openLinkInNewTab: PropTypes.func.isRequired,
  childID: PropTypes.string,
  pathname: PropTypes.string,
};

UpcomingMeetingNotification.defaultProps = {
  childID: "",
  pathname: "",
};

export default UpcomingMeetingNotification;
